import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4PetLiabilityArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Does Renters Insurance Cover Dog Bites and Pet Liability? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Find out whether renters insurance covers dog bites, pet liability claims, and breed restrictions — and when you may need extra coverage in Texas.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/does-renters-insurance-cover-dog-bites-and-pet-liability"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Does renters insurance cover dog bites and pet liability?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              A dog bite claim can exceed $50,000 in medical bills and legal fees — personal liability on your HO-4 policy is often your first line of defense.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Most HO-4 renters insurance policies include personal liability coverage that can pay for dog bite injuries
                and property damage your pet causes to others — up to your policy limit. Some insurers exclude certain breeds
                or require a separate pet liability endorsement. Verify your breed and limit before you need to file a claim.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What pet liability coverage typically pays for</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Medical bills for a guest or neighbor bitten by your dog.</li>
                <li style={{ marginBottom: '0.5rem' }}>Legal defense costs if you are sued after a pet-related incident.</li>
                <li style={{ marginBottom: '0.5rem' }}>Damage your pet causes to someone else&apos;s property — chewed furniture, scratched doors, etc.</li>
                <li>
                  Injuries at dog parks or on walks, subject to policy terms and any off-premises limits.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Breed restrictions and exclusions</h3>
              <p style={{ marginBottom: '0.75rem' }}>
                Some carriers maintain a restricted breed list (often including pit bulls, Rottweilers, and similar breeds)
                and will exclude liability coverage for those dogs unless you purchase a separate endorsement or specialty policy.
                Other insurers evaluate each dog individually. Always disclose pets when buying a policy — undisclosed pets can void coverage.
              </p>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Ask your carrier about breed restrictions before enrolling.</li>
                <li style={{ marginBottom: '0.5rem' }}>Consider an umbrella policy if you own a high-risk breed or multiple dogs.</li>
                <li>
                  Check whether your lease bans certain breeds — that is separate from insurance but affects your tenancy.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How much liability do you need?</h3>
              <p style={{ marginBottom: 0 }}>
                Dog bite claims can easily exceed $100,000. Many renters carry $100,000 in liability, but $300,000 is a common
                upgrade for pet owners. Read{' '}
                <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>
                  how much liability you need
                </Link>{' '}
                and consider whether your limit is enough if your dog injures someone seriously.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What renters insurance does not cover for pets</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Veterinary bills for your own pet — that requires pet insurance, not HO-4.</li>
                <li style={{ marginBottom: '0.5rem' }}>Damage your pet causes to your own belongings or rental unit (may fall under personal property or landlord damage rules).</li>
                <li>Incidents involving a restricted breed if your policy excludes that breed.</li>
              </ul>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Compare HO-4 policies with adequate liability limits for pet owners in Texas.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Own a restricted breed or multiple pets?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can help you find carriers that cover your breed and recommend appropriate liability limits.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4PetLiabilityArticlePage;
